import React from 'react';
import { useState, useRef } from 'react';
import { auth, googleProvider } from '../lib/firebase';
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  type MultiFactorResolver,
} from 'firebase/auth';
import { Wallet, LogIn, UserPlus, ShieldCheck } from 'lucide-react';

export default function AuthForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Set only when Firebase rejects sign-in with auth/multi-factor-auth-required
  // -- the password (or Google) step already succeeded, this is the second
  // factor. Nothing above completes sign-in until this resolves.
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaFactorType, setMfaFactorType] = useState<'phone' | 'totp' | null>(null);
  const [mfaHintUid, setMfaHintUid] = useState<string | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  const getVerifier = () => {
    if (!verifierRef.current && recaptchaRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, { size: 'invisible' });
    }
    return verifierRef.current!;
  };

  const startMfaChallenge = async (resolver: MultiFactorResolver) => {
    setMfaResolver(resolver);
    // Prefer TOTP when it's one of the enrolled factors -- unlike phone,
    // it needs no async "send a code" round-trip (no reCAPTCHA, no SMS),
    // so the code entry screen can render immediately.
    const totpHint = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (totpHint) {
      setMfaFactorType('totp');
      setMfaHintUid(totpHint.uid);
      return;
    }
    setMfaFactorType('phone');
    const hint = resolver.hints[0];
    const provider = new PhoneAuthProvider(auth);
    const id = await provider.verifyPhoneNumber({ multiFactorHint: hint, session: resolver.session }, getVerifier());
    setMfaVerificationId(id);
  };

  const handleVerifyMfaCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaResolver) return;
    setError('');
    setLoading(true);
    try {
      const assertion = mfaFactorType === 'totp' && mfaHintUid
        ? TotpMultiFactorGenerator.assertionForSignIn(mfaHintUid, mfaCode)
        : (() => {
            if (!mfaVerificationId) throw new Error('Missing verification session -- please try again.');
            const cred = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
            return PhoneMultiFactorGenerator.assertion(cred);
          })();
      await mfaResolver.resolveSignIn(assertion);
      // onAuthStateChanged in App.tsx picks up the now-fully-signed-in user from here.
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        try {
          // Required before this user can enroll any MFA factor (a real
          // Identity Platform precondition, not optional polish) -- send it
          // right away so verifying is just "click the link in your inbox"
          // rather than a separate step someone has to remember to trigger.
          await sendEmailVerification(cred.user);
        } catch (verifyErr) {
          // Non-fatal: the account itself was created successfully. The
          // user can resend from Settings -> Security if this call failed.
          console.error('Failed to send verification email:', verifyErr);
        }
      }
    } catch (err: any) {
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          await startMfaChallenge(getMultiFactorResolver(auth, err));
        } catch (mfaErr: any) {
          setError(mfaErr.message || 'Failed to start two-factor verification.');
        }
      } else {
        setError(err.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err: any) {
      if (err.code === 'auth/multi-factor-auth-required') {
        try {
          await startMfaChallenge(getMultiFactorResolver(auth, err));
        } catch (mfaErr: any) {
          setError(mfaErr.message || 'Failed to start two-factor verification.');
        }
      } else {
        setError(err.message || 'Google sign-in failed');
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 w-full">
      {mfaResolver ? (
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="p-8 text-center bg-slate-900 text-white">
            <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-blue-500" />
            <h1 className="text-2xl font-bold tracking-tight">Two-Factor Verification</h1>
            <p className="text-slate-400 mt-2 text-sm">
              {mfaFactorType === 'totp' ? 'Enter the code from your authenticator app' : 'Enter the code sent to your phone'}
            </p>
          </div>
          <div className="p-8">
            <form onSubmit={handleVerifyMfaCode} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">{error}</div>
              )}
              <input
                type="text"
                inputMode="numeric"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white transition-all"
                placeholder="6-digit code"
                required
                autoFocus
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-2.5 px-4 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Verify'
                )}
              </button>
              <button
                type="button"
                onClick={() => { setMfaResolver(null); setMfaFactorType(null); setMfaHintUid(null); setMfaVerificationId(null); setMfaCode(''); setError(''); }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Back to sign in
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="p-8 text-center bg-slate-900 text-white">
            <Wallet className="w-12 h-12 mx-auto mb-4 text-blue-500" />
            <h1 className="text-2xl font-bold tracking-tight">Tabulario</h1>
            <p className="text-slate-400 mt-2 text-sm">Take control of your finances</p>
          </div>

          <div className="p-8">
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white transition-all"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white transition-all"
                  placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-2.5 px-4 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isLogin ? (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between">
              <span className="border-b border-slate-200 w-1/5 lg:w-1/4"></span>
              <span className="text-xs text-center text-slate-500 uppercase font-bold tracking-wider">Or continue with</span>
              <span className="border-b border-slate-200 w-1/5 lg:w-1/4"></span>
            </div>

            <button
              onClick={handleGoogleAuth}
              className="mt-6 w-full flex items-center justify-center py-2.5 px-4 bg-white border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 shadow-sm transition-colors text-sm font-semibold"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>

            <p className="mt-8 text-center text-sm text-slate-600">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-600 font-bold hover:underline focus:outline-none"
              >
                {isLogin ? 'Sign up' : 'Log in'}
              </button>
            </p>
          </div>
        </div>
      )}
      {/* Invisible reCAPTCHA -- rendered as a sibling of both screens above
          (not inside either), so it never unmounts mid-flight when
          startMfaChallenge's setMfaResolver() swaps which screen is
          showing while its own verifyPhoneNumber() call (using this same
          container) is still in progress. It used to live only inside the
          sign-in form, and unmounting it right as the MFA screen appeared
          could surface a real "reCAPTCHA client element has been removed"
          error from the in-flight call. */}
      <div ref={recaptchaRef} />

      {!mfaResolver && (
        <p className="mt-6 text-center text-xs text-slate-400">
          By continuing you agree to our{' '}
          <a
            href="https://wecrafttech.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-600"
          >
            Privacy Policy
          </a>
        </p>
      )}
    </div>
  );
}
